/**
 * Une lo que tiene el servidor con lo que tiene el navegador antes de guardar
 * los documentos de una postulacion.
 *
 * Hace falta porque el backend reemplaza la columna entera con lo que le
 * mande el cliente: guardarDocumentosAdministrativos hace un update de
 * documentos_administrativos con el cuerpo de la peticion, sin fusionar ni
 * controlar concurrencia. Si el navegador tiene una copia vieja, al guardar
 * borra lo que no conoce. Es lo que paso en el caso C466-013, el documento
 * que desaparecio.
 *
 * La regla no puede ser "nunca borrar", porque la pantalla si permite
 * eliminar un archivo a proposito. Por eso se reciben aparte las direcciones
 * que se eliminaron en esta sesion: esas si se dejan ir.
 */

/** La llave estable de un documento; el id, y si falta, la descripcion. */
const llave = (documento: any): string =>
  documento?.id !== undefined && documento?.id !== null
    ? `id:${documento.id}`
    : `desc:${documento?.descripcion ?? ""}`;

const tieneArchivo = (documento: any): boolean =>
  typeof documento?.url_participante === "string" &&
  documento.url_participante !== "";

export const fusionarDocumentos = (
  delServidor: any[],
  delCliente: any[],
  urlsBorradas: string[] = []
): any[] => {
  const servidor = Array.isArray(delServidor) ? delServidor : [];
  const cliente = Array.isArray(delCliente) ? delCliente : [];

  if (servidor.length === 0) return cliente;

  const porLlave = new Map<string, any>();
  servidor.forEach((documento) => porLlave.set(llave(documento), documento));

  const borradas = new Set(urlsBorradas.filter(Boolean));
  const vistas = new Set<string>();

  const fusionados = cliente.map((documentoCliente) => {
    const clave = llave(documentoCliente);
    vistas.add(clave);

    const documentoServidor = porLlave.get(clave);
    if (!documentoServidor) return documentoCliente;

    // Si el cliente ya trae archivo, manda el cliente: es lo que la persona
    // acaba de subir.
    if (tieneArchivo(documentoCliente)) return documentoCliente;

    // El cliente no trae archivo. Si el servidor si, y no se borro a
    // proposito, es que esta copia esta vieja: se conserva lo del servidor.
    if (tieneArchivo(documentoServidor) && !borradas.has(documentoServidor.url_participante)) {
      return {
        ...documentoCliente,
        url_participante: documentoServidor.url_participante,
        fecha_firma: documentoCliente.fecha_firma || documentoServidor.fecha_firma,
        participantes:
          documentoCliente.participantes?.length > 0
            ? documentoCliente.participantes
            : documentoServidor.participantes,
      };
    }

    return documentoCliente;
  });

  // Un documento que el servidor tiene y el navegador ni siquiera conoce no
  // puede perderse por guardar.
  const faltantes = servidor.filter((documento) => !vistas.has(llave(documento)));

  return [...fusionados, ...faltantes];
};

/** Cuantos documentos se habrian perdido de no fusionar. */
export const documentosRescatados = (
  delServidor: any[],
  delCliente: any[],
  urlsBorradas: string[] = []
): number => {
  const fusionados = fusionarDocumentos(delServidor, delCliente, urlsBorradas);
  const conArchivoCliente = (Array.isArray(delCliente) ? delCliente : []).filter(tieneArchivo).length;
  const conArchivoFusion = fusionados.filter(tieneArchivo).length;
  return Math.max(0, conArchivoFusion - conArchivoCliente);
};
